import React, { createContext, useContext, useState, useCallback } from 'react'
import { useEditor } from '@toasttab/sites-components'
import { MenuGroup } from '../default_template/menu_banner/types'

type MenuGroupingsContextType = {
  groupings: MenuGroup[] | undefined
  setGroupings: (groupings: MenuGroup[] | undefined) => void
}

export const MenuGroupingsContext = createContext<
  MenuGroupingsContextType | undefined
>(undefined)

export const useMenuGroupings = () => {
  const context = useContext(MenuGroupingsContext)
  const { isEditor } = useEditor()
  if (isEditor) {
    return { groupings: undefined, setGroupings: () => {} }
  }
  if (!context) {
    throw new Error(
      'useMenuGroupings must be used within MenuGroupingsProvider'
    )
  }
  return context
}

/**
 * Provider for persisting menu groupings across category navigation. In the editor, no-op
 */
export const MenuGroupingsProvider = ({
  children
}: React.PropsWithChildren) => {
  const { isEditor } = useEditor()
  return isEditor ? (
    <EmptyMenuGroupingsProvider>{children}</EmptyMenuGroupingsProvider>
  ) : (
    <SitesMenuGroupingsProvider>{children}</SitesMenuGroupingsProvider>
  )
}

const EmptyMenuGroupingsProvider = ({ children }: React.PropsWithChildren) => {
  return (
    <MenuGroupingsContext.Provider
      value={{ groupings: undefined, setGroupings: () => {} }}
    >
      {children}
    </MenuGroupingsContext.Provider>
  )
}

const SitesMenuGroupingsProvider = ({ children }: React.PropsWithChildren) => {
  const [groupings, setGroupings] = useState<MenuGroup[] | undefined>(undefined)

  const updateGroupings = useCallback((groupings: MenuGroup[] | undefined) => {
    setGroupings(groupings)
  }, [])

  return (
    <MenuGroupingsContext.Provider
      value={{ groupings, setGroupings: updateGroupings }}
    >
      {children}
    </MenuGroupingsContext.Provider>
  )
}
